import { useState, useEffect } from 'react';
import { setPreviewToken } from '~/api/previewTokenStore';

// These must match the constants defined in apps/pulse/client/src/views/Main.section/Main.layout/Main.layout.tsx
export const PREVIEW_TOKEN_REQUEST = 'WORKTANGO_REQUEST_PREVIEW_TOKEN';
export const PREVIEW_TOKEN_RESPONSE = 'WORKTANGO_PREVIEW_AUTH_TOKEN';

/**
 * When in preview mode, requests a JWT from the admin client window that
 * opened this tab via postMessage, then stores it in the in-memory token
 * store so Apollo can attach it to previewEmployeeSearch requests.
 *
 * Returns `tokenReady: true` once the token has been received and stored.
 * The combobox should not fire its initial fetch until tokenReady is true,
 * since the postMessage round-trip is async and the fetch would otherwise
 * race ahead of the token arrival.
 *
 * Security:
 * - Token never touches localStorage, the URL, or the network in clear text.
 * - Only accepts the token from window.opener (the window that opened this tab).
 * - Message types are namespaced to avoid conflicts with other postMessage users.
 */
export function usePreviewToken(isPreview) {
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (!isPreview || !window.opener) return;

    function handleMessage(event) {
      if (event.source !== window.opener) return;
      if (event.data?.type !== PREVIEW_TOKEN_RESPONSE) return;
      if (typeof event.data.token !== 'string') return;
      setPreviewToken(event.data.token);
      setTokenReady(true);
    }

    window.addEventListener('message', handleMessage);
    window.opener.postMessage({ type: PREVIEW_TOKEN_REQUEST }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, [isPreview]);

  return tokenReady;
}
